import React, { type ReactNode } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  type Placement,
} from '@floating-ui/react';

/**
 * Drop-in replacement for the bits of `react-popover` this app used.
 *
 * react-popover called `ReactDOM.findDOMNode` in `componentDidMount`, which is
 * removed in React 19 and crashes on mount. This rebuilds the same behaviour on
 * `@floating-ui/react` while preserving the markup contract that the
 * `.Popover-{side} .Popover-body` arrow CSS in `src/styles/popover.css` keys on:
 *
 *   <div class="Popover Popover-{side}">
 *     <div class="Popover-body">{body}</div>
 *   </div>
 *
 * The arrow is drawn entirely by the `.Popover-{side} .Popover-body` pseudo
 * elements in popover.css (the original `.Popover-tip` SVG is no longer
 * rendered), so no tip node is rendered here.
 *
 * `preferPlace` keeps react-popover's vocabulary ("below"/"above"/"left"/
 * "right"/"row"/"column") and maps onto a floating-ui placement; `flip()` then
 * mirrors react-popover's "pick the side that fits" behaviour. The trigger keeps
 * its hover handlers and wrapper class so styling and behaviour are identical.
 */

/** The side classes popover.css draws arrows for. */
type Side = 'below' | 'above' | 'left' | 'right';

/**
 * Map react-popover's `preferPlace` vocabulary to a floating-ui placement.
 * "row" prefers a horizontal side, "column" a vertical one; `flip()` handles
 * the fit. Unknown/empty values fall back to react-popover's own default
 * (below).
 */
function preferPlaceToPlacement(preferPlace?: string | null): Placement {
  switch (preferPlace) {
    case 'above':
      return 'top';
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    case 'row':
      // Horizontal axis preferred; default to the right, flip() finds the fit.
      return 'right';
    case 'below':
    case 'column':
    default:
      return 'bottom';
  }
}

/** Resolve floating-ui's actual placement back to a popover.css side class. */
function placementToSide(placement: Placement): Side {
  const base = placement.split('-')[0];
  switch (base) {
    case 'top':
      return 'above';
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    case 'bottom':
    default:
      return 'below';
  }
}

interface FloatingPopoverProps {
  /** Whether the popover is shown. */
  isOpen: boolean;
  /** The popover body content. */
  body: ReactNode;
  /** Preferred placement, in react-popover's vocabulary. */
  preferPlace?: string | null;
  /** The trigger element the popover anchors to. */
  children: ReactNode;
}

export default function FloatingPopover({ isOpen, body, preferPlace, children }: FloatingPopoverProps) {
  const { refs, floatingStyles, placement } = useFloating({
    open: isOpen,
    placement: preferPlaceToPlacement(preferPlace),
    // react-popover sat the body `tipSize (7) + offset (4)` = 11px off the
    // trigger; keep the same gap so the arrow lands in the same spot.
    middleware: [offset(11), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const side = placementToSide(placement);

  return (
    <>
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<{ ref?: React.Ref<unknown> }>, {
            ref: refs.setReference,
          })
        : children}
      {isOpen && (
        <FloatingPortal>
          <div ref={refs.setFloating} className={`Popover Popover-${side} z-[100]`} style={floatingStyles}>
            <div className="Popover-body relative z-[1] border border-grey bg-white p-2.5 rounded-md shadow-sm">
              {body}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
